'use client';

import { c as _c } from "react/compiler-runtime";
import type { GithubFile } from '@/types/github';
import { FileCode } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
type MentionableFilesDropdownProps = {
  files: GithubFile[];
  onSelect: (file: {
    filename: string;
  }) => void;
  searchQuery: string;
  listboxId?: string;
  onActiveOptionIdChange?: (value: string | undefined) => void;
};
const FileCommandItem = t0 => {
  const $ = _c(29);
  const {
    file,
    onSelect,
    isSelected,
    onMouseEnter,
    optionId
  } = t0;
  const lastSlashIndex = file.filename.lastIndexOf("/");
  let t1;
  if ($[0] !== file.filename || $[1] !== lastSlashIndex) {
    t1 = lastSlashIndex !== -1 ? file.filename.substring(lastSlashIndex + 1) : file.filename;
    $[0] = file.filename;
    $[1] = lastSlashIndex;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const fileName = t1;
  let t2;
  if ($[3] !== file.filename || $[4] !== lastSlashIndex) {
    t2 = lastSlashIndex !== -1 ? file.filename.substring(0, lastSlashIndex + 1) : "";
    $[3] = file.filename;
    $[4] = lastSlashIndex;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const filePath = t2;
  const t3 = isSelected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50";
  let t4;
  if ($[6] !== t3) {
    t4 = cn("flex items-center gap-2 cursor-pointer py-1.5 px-2 rounded-sm", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = isSelected ? "selected" : undefined;
  const t6 = isSelected ? "text-accent-foreground" : "text-muted-foreground";
  let t7;
  if ($[8] !== t6) {
    t7 = cn("size-4 shrink-0", t6);
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = <FileCode className={t7} />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== fileName) {
    t9 = <span className="text-sm whitespace-nowrap">{fileName}</span>;
    $[12] = fileName;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== filePath || $[15] !== isSelected) {
    t10 = filePath && <span className={cn("text-xs whitespace-nowrap truncate", isSelected ? "text-accent-foreground/70" : "text-muted-foreground")} style={{
      direction: "rtl",
      textAlign: "left"
    }}>{filePath}</span>;
    $[14] = filePath;
    $[15] = isSelected;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <div className="flex flex-1 items-center gap-1 overflow-hidden">{t9}{t10}</div>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== isSelected || $[21] !== onMouseEnter || $[22] !== onSelect || $[23] !== optionId || $[24] !== t11 || $[25] !== t4 || $[26] !== t5 || $[27] !== t8) {
    t12 = <div id={optionId} onClick={onSelect} onMouseEnter={onMouseEnter} role="option" aria-selected={isSelected} className={t4} data-index={t5}>{t8}{t11}</div>;
    $[20] = isSelected;
    $[21] = onMouseEnter;
    $[22] = onSelect;
    $[23] = optionId;
    $[24] = t11;
    $[25] = t4;
    $[26] = t5;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};
export const MentionableFilesDropdown = t0 => {
  const $ = _c(28);
  const {
    files,
    onSelect,
    searchQuery,
    listboxId,
    onActiveOptionIdChange
  } = t0;
  const idPrefix = useId();
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t1;
  if ($[0] !== files || $[1] !== searchQuery) {
    const searchTerm = searchQuery.slice(1).toLowerCase();
    t1 = searchTerm ? files.filter(file => file.filename.toLowerCase().includes(searchTerm)) : files.slice(0, 10);
    $[0] = files;
    $[1] = searchQuery;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const filteredFiles = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      queueMicrotask(() => {
        setSelectedIndex(0);
      });
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== filteredFiles) {
    t3 = [filteredFiles];
    $[4] = filteredFiles;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== selectedIndex) {
    t4 = [selectedIndex];
    $[6] = selectedIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useLayoutEffect(_temp, t4);
  let t5;
  if ($[8] !== onSelect) {
    t5 = file_0 => {
      onSelect({
        filename: file_0.filename
      });
      setSelectedIndex(0);
    };
    $[8] = onSelect;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleSelection = t5;
  const activeOptionId = filteredFiles.length > 0 && selectedIndex >= 0 && selectedIndex < filteredFiles.length ? `${idPrefix}-mentionable-file-option-${selectedIndex}` : undefined;
  let t6;
  let t7;
  if ($[10] !== activeOptionId || $[11] !== onActiveOptionIdChange) {
    t6 = () => {
      onActiveOptionIdChange?.(activeOptionId);
    };
    t7 = [activeOptionId, onActiveOptionIdChange];
    $[10] = activeOptionId;
    $[11] = onActiveOptionIdChange;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== filteredFiles || $[15] !== handleSelection || $[16] !== onSelect || $[17] !== selectedIndex) {
    t8 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onSelect({
            filename: ""
          });
          return;
        }
        if (filteredFiles.length === 0) {
          return;
        }
        bb40: switch (e.key) {
          case "ArrowDown":
            {
              e.preventDefault();
              setSelectedIndex(prev_0 => Math.min(prev_0 + 1, filteredFiles.length - 1));
              break bb40;
            }
          case "ArrowUp":
            {
              e.preventDefault();
              setSelectedIndex(_temp2);
              break bb40;
            }
          case "Enter":
          case "Tab":
            {
              e.preventDefault();
              e.stopPropagation();
              const selectedFile = filteredFiles[selectedIndex];
              if (selectedFile) {
                handleSelection(selectedFile);
              }
            }
        }
      };
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t9 = [filteredFiles, selectedIndex, handleSelection, onSelect];
    $[14] = filteredFiles;
    $[15] = handleSelection;
    $[16] = onSelect;
    $[17] = selectedIndex;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  if ($[20] !== filteredFiles || $[21] !== handleSelection || $[22] !== idPrefix || $[23] !== selectedIndex) {
    t10 = filteredFiles.length === 0 ? <div role="option" aria-disabled={true} className="p-2 text-sm text-muted-foreground">No files found.</div> : filteredFiles.map((file_1, index) => <FileCommandItem key={file_1.filename} file={file_1} optionId={`${idPrefix}-mentionable-file-option-${index}`} isSelected={index === selectedIndex} onSelect={() => handleSelection(file_1)} onMouseEnter={() => setSelectedIndex(index)} />);
    $[20] = filteredFiles;
    $[21] = handleSelection;
    $[22] = idPrefix;
    $[23] = selectedIndex;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== listboxId || $[26] !== t10) {
    t11 = <div className="rounded-lg border shadow-md bg-popover"><div id={listboxId} className="max-h-[300px] overflow-y-auto p-1" role="listbox" aria-label="Mentionable files">{t10}</div></div>;
    $[25] = listboxId;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
function _temp() {
  const selectedElement = document.querySelector("[data-index=\"selected\"]");
  if (selectedElement) {
    selectedElement.scrollIntoView({
      block: "nearest",
      behavior: "smooth"
    });
  }
}
function _temp2(prev) {
  return Math.max(prev - 1, 0);
}